// V5.26 REFONTE ARCHITECTURE : Mission Portal avec silos étanches
// Tunnel : Adresses → Stratégie → Collecte Technique → Mandat → Confirmation
// Prix du service : 179€
// V5.26 : Architecture missionData unifiée avec sync permanente
import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import AddressAutocomplete from '../components/ui/AddressAutocomplete';
import { Checkbox } from '../components/ui/checkbox';
import MandatSignature from '../components/MandatSignature';
import ErrorBoundary from '../components/ErrorBoundary';
import {
  Home, Shield, Upload, CheckCircle, FileText, MapPin, Calendar,
  Building2, Zap, Droplets, Wifi, Phone, ArrowRight, ArrowLeft,
  Loader2, AlertCircle, HelpCircle, X, MessageCircle, Sparkles, Brain, Menu, User, Save
} from 'lucide-react';

// V5 REFONDATION : Import des composants Sidebar Contextuelle
import { ContextualSidebar, MobileMissionStepper, MobileMenu } from '../components/layout/ContextualSidebar';
import { MISSION_CONFIG, normalizeMissionType } from '../components/missions/shared/MissionComponents';

// V5.26 : Nouveaux composants du tunnel refactorisés
import StrategyStepV526 from '../components/missions/StrategyStepV526';
import TechnicalCollectionStepV526 from '../components/missions/TechnicalCollectionStepV526';
import FeeDisclaimerConsent from '../components/missions/FeeDisclaimerConsent';

// V5.26 : Import du gestionnaire de données centralisé
import {
  generateMissionId as generateMissionIdUtil,
  createEmptyMissionData,
  cleanSlateProtocol,
  saveMissionData,
  loadMissionData,
  getActiveMissionId,
  prepareDataForBackend,
  validateStep1,
  validateStep2,
  validateStep3,
  validateBeforePayment
} from '../utils/missionDataManager';

// V5.14 : Anciens composants conservés pour compatibilité
import StrategyStep from '../components/missions/StrategyStep';
import TechnicalCollectionStep from '../components/missions/TechnicalCollectionStep';
import DocumentsStep from '../components/missions/DocumentsStep';

// V5.32 : Import de la vue Dossier Validé
import ValidatedMissionView from '../components/missions/ValidatedMissionView';

// V5.26 : Réexport des fonctions utilitaires depuis le gestionnaire centralisé
export { cleanSlateProtocol as clearPreviousData } from '../utils/missionDataManager';

// V5.26 : Fonction locale de génération d'ID (pour compatibilité)
const generateMissionId = generateMissionIdUtil;

// V5.32 : Configuration des portails avec tunnel simplifié (4 étapes)
const PORTAL_CONFIG = {
  demenagement: {
    title: "Mission Déménagement",
    subtitle: "Service d'expertise juridique - 179€",
    icon: Home,
    color: "cyan",
    bgGradient: "from-cyan-500/20 to-blue-500/20",
    // V5.32 : Tunnel simplifié - 4 étapes (sans étape intermédiaire)
    steps: ["Adresses", "Stratégie", "Technique", "Synthèse"],
    price: 179
  },
  succession: {
    title: "Mission Héritage",
    subtitle: "Résiliation bienveillante des contrats du défunt",
    icon: Shield,
    color: "magenta",
    bgGradient: "from-magenta-500/20 to-purple-500/20",
    // V1.2 : alignement sur le tunnel Déménagement (4 étapes) mais uniquement
    // résiliation (pas de transfert puisque le titulaire est décédé).
    steps: ["Défunt", "Stratégie", "Technique", "Mandat"],
    price: 199
  },
  audit: {
    title: "Mission Audit",
    subtitle: "Analyse de vos abonnements",
    icon: Zap,
    color: "success",
    bgGradient: "from-success/20 to-green-500/20",
    steps: ["Documents", "Confirmation"]
  }
};

// Contrats disponibles pour le déménagement
const CONTRACTS = [
  { id: 'electricite', name: 'Électricité', icon: Zap, required: true },
  { id: 'gaz', name: 'Gaz', icon: Zap, required: false },
  { id: 'eau', name: 'Eau', icon: Droplets, required: false },
  { id: 'internet', name: 'Internet / Box', icon: Wifi, required: true },
  { id: 'mobile', name: 'Téléphone Mobile', icon: Phone, required: false },
  { id: 'assurance', name: 'Assurance Habitation', icon: Shield, required: true }
];

// Organismes connus pour la succession
const KNOWN_ORGANISMS = [
  { id: 'banque', name: 'Banques', examples: 'BNP, Crédit Agricole, LCL...' },
  { id: 'assurance', name: 'Assurances', examples: 'AXA, Allianz, MAIF...' },
  { id: 'mutuelle', name: 'Mutuelles', examples: 'MGEN, Harmonie, MMA...' },
  { id: 'energie', name: 'Énergie', examples: 'EDF, Engie, TotalEnergies...' },
  { id: 'telecom', name: 'Télécom', examples: 'Orange, Free, SFR, Bouygues...' },
  { id: 'presse', name: 'Presse/Abonnements', examples: 'Journaux, magazines...' }
];

const MissionPortalPage = () => {
  const { type: paramType } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  
  // V5.45 : Détecter le type (pack_type) depuis le chemin URL si pas de paramètre
  const type = paramType || (() => {
    if (location.pathname.includes('/missions/demenagement')) return 'demenagement';
    if (location.pathname.includes('/missions/succession')) return 'succession';
    if (location.pathname.includes('/missions/audit')) return 'audit';
    return 'demenagement';
  })();
  
  // V5.45 : Gestion de l'ID de session unique - Format EFF-[TIMESTAMP]-[RANDOM]
  const searchParams = new URLSearchParams(location.search);
  const urlMissionId = searchParams.get('mission');
  // V1.2 PAYWALL : forcer une mission neuve aussi quand on revient d'un paiement Stripe
  // réussi (sinon le localStorage recharge l'ancienne mission déjà remplie).
  const justPaid =
    !!searchParams.get('session_id') &&
    searchParams.get('payment') === 'success';
  const forceNew = searchParams.get('new') === 'true' || justPaid;
  
  // V5.45 : État principal unifié - SOURCE DE VÉRITÉ UNIQUE avec pack_type
  const [missionData, setMissionData] = useState(() => {
    // V1.2 ÉTANCHÉITÉ : helper - une mission déjà signée ou validée ne doit JAMAIS
    // être rechargée dans le tunnel (sinon le client retombe à l'étape 4 d'une
    // ancienne mission). Si l'active ID pointe sur une mission terminée, on en crée
    // une neuve pour le rachat.
    const isCompletedMission = (m) =>
      !!m && (m.signed_at || m.status === 'VALIDATED' || m.mission_validated === true);

    // V5.45 : Si forceNew=true, créer une nouvelle mission avec Clean Slate pour CE pack_type
    if (forceNew) {
      const newId = generateMissionId();
      cleanSlateProtocol(type, newId); // V5.45 : Clean Slate ciblé par pack_type
      const newMission = createEmptyMissionData(newId, null, type); // V5.45 : Inclure pack_type
      saveMissionData(newMission);
      console.log(`🆕 V5.45 Nouvelle mission créée: ${newId} (pack: ${type})`);
      return newMission;
    }

    // V5.45 : Si on a un ID dans l'URL, essayer de charger avec le bon pack_type
    if (urlMissionId) {
      const loaded = loadMissionData(urlMissionId, type);
      if (loaded) {
        // V5.45 : Vérifier que le pack_type correspond
        if (loaded.pack_type && loaded.pack_type !== type) {
          console.warn(`⚠️ V5.45: pack_type mismatch (${loaded.pack_type} vs ${type}), création nouvelle mission`);
          const newId = generateMissionId();
          const newMission = createEmptyMissionData(newId, null, type);
          saveMissionData(newMission);
          return newMission;
        }
        return loaded;
      }
    }

    // V5.45 : Vérifier s'il y a une mission active pour CE pack_type spécifique
    const activeId = getActiveMissionId(type);
    if (activeId) {
      const loaded = loadMissionData(activeId, type);
      // V1.2 ÉTANCHÉITÉ : ne pas recharger une mission déjà signée/validée
      if (loaded && !isCompletedMission(loaded)) {
        console.log(`📂 V5.45: Mission active trouvée pour ${type}: ${activeId}`);
        return loaded;
      }
      if (loaded && isCompletedMission(loaded)) {
        console.log(`🔒 V1.2 Mission ${activeId} déjà signée - on crée une mission neuve pour le rachat`);
        cleanSlateProtocol(type, null);
      }
    }

    // V5.45 : Sinon, créer une nouvelle mission pour CE pack_type
    const newMission = createEmptyMissionData(null, null, type);
    saveMissionData(newMission);
    console.log(`🆕 V5.45 Nouvelle mission créée: ${newMission.id} (pack: ${type})`);
    return newMission;
  });
  
  // V5.45 : ID local de mission (extrait de missionData)
  const localMissionId = missionData?.id;
  
  // V5.45 : Handler de mise à jour de missionData avec sync permanente
  const updateMissionData = useCallback((newData) => {
    // V5.45 : S'assurer que pack_type est toujours présent
    const dataWithPackType = { ...newData, pack_type: type };
    setMissionData(dataWithPackType);
    saveMissionData(dataWithPackType);
  }, [type]);
  
  // V5.45 : Clés de stockage avec pack_type (pour compatibilité)
  const getStorageKey = useCallback((suffix) => `effaceur_mission_${type}_${localMissionId}_${suffix}`, [type, localMissionId]);
  
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [currentStep, setCurrentStep] = useState(missionData?.current_step || 1);
  const [showHelper, setShowHelper] = useState(false);
  const [inactivityTimer, setInactivityTimer] = useState(null);
  const [savingProgress, setSavingProgress] = useState(false);  // DDD.7
  const fileInputRef = useRef(null);

  // DDD.10 : auto-save backend debounced (2s après dernière modification de missionData).
  // Avant : les données techniques (PDL, PCE, notes complémentaires) en succession n'étaient
  // persistées QUE sur clic explicite "Sauvegarder & reprendre plus tard". Maintenant chaque
  // modification de missionData (catégorie, champ technique, etc.) est auto-sauvegardée.
  const autoSaveTimerRef = useRef(null);
  useEffect(() => {
    if (!missionData?.id) return;
    if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    autoSaveTimerRef.current = setTimeout(async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) return;
        await axios.post(
          `${API}/missions/${missionData.id}/save-progress`,
          {
            form_data: type === 'demenagement' ? demenagementData : (typeof successionData !== 'undefined' ? successionData : {}),
            current_step: currentStep,
            mission_data: missionData,
            mission_type: type,
          },
          { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
        );
      } catch (_) { /* silencieux — auto-save best-effort */ }
    }, 2000);
    return () => {
      if (autoSaveTimerRef.current) clearTimeout(autoSaveTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missionData, currentStep, type]);

  // DDD.7 : Sauvegarde progressive — "Sauvegarder & reprendre plus tard"
  // Persiste l'état du tunnel côté backend ET localStorage, puis renvoie au dashboard.
  // Le client retrouvera son tunnel exactement où il l'avait laissé.
  const handleSaveAndExit = useCallback(async () => {
    if (savingProgress) return;
    setSavingProgress(true);
    try {
      // 1. Sauvegarde locale (immédiate, ne dépend pas du réseau)
      const dataWithStep = { ...missionData, current_step: currentStep };
      saveMissionData(dataWithStep);

      // 2. Sauvegarde backend (si on a un mission_id en base)
      const token = localStorage.getItem('token');
      const idToPersist = missionData?.id || urlMissionId || localMissionId;
      if (token && idToPersist) {
        try {
          await axios.post(
            `${API}/missions/${idToPersist}/save-progress`,
            {
              form_data: demenagementData,
              current_step: currentStep,
              mission_data: dataWithStep,
              mission_type: type,
            },
            { headers: { Authorization: `Bearer ${token}` }, withCredentials: true }
          );
        } catch (apiErr) {
          // On ne bloque pas si le backend échoue — la copie localStorage est OK
          console.warn('save-progress backend failed (localStorage OK):', apiErr?.response?.status);
        }
      }

      toast.success('Progression sauvegardée. Tu peux reprendre quand tu veux depuis Mes Missions.');
      navigate('/dashboard?tab=missions');
    } catch (e) {
      toast.error('Impossible de sauvegarder. Réessaie dans un instant.');
    } finally {
      setSavingProgress(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [savingProgress, missionData, currentStep, type, urlMissionId, localMissionId]);
  
  // V5.26 : Synchronisation du currentStep avec missionData
  useEffect(() => {
    if (missionData?.current_step && missionData.current_step !== currentStep) {
      setCurrentStep(missionData.current_step);
    }
  }, [missionData?.current_step]);
  
  // V5.26 : Mise à jour de l'étape dans missionData
  const updateCurrentStepInMission = useCallback((step) => {
    setCurrentStep(step);
    updateMissionData({
      ...missionData,
      current_step: step
    });
  }, [missionData, updateMissionData]);
  
  // V5.45 : Extraction des données avec first_name/last_name séparés
  const demenagementData = {
    // V5.45 : FIL CONDUCTEUR - Prénom/Nom/Téléphone/Email séparés
    first_name: missionData?.client?.first_name || missionData?.first_name || '',
    last_name: missionData?.client?.last_name || missionData?.last_name || '',
    telephone: missionData?.client?.telephone || missionData?.telephone || '',
    email: missionData?.client?.email || missionData?.email || '', // V5.45: Email mappé
    // Adresses
    ancienne_adresse: missionData?.step1_adresses?.depart?.adresse || '',
    ancienne_ville: missionData?.step1_adresses?.depart?.ville || '',
    ancienne_code_postal: missionData?.step1_adresses?.depart?.code_postal || '',
    nouvelle_adresse: missionData?.step1_adresses?.arrivee?.adresse || '',
    nouvelle_ville: missionData?.step1_adresses?.arrivee?.ville || '',
    nouvelle_code_postal: missionData?.step1_adresses?.arrivee?.code_postal || '',
    date_demenagement: missionData?.step1_adresses?.date_demenagement || '',
    contrats_transfert: [],
    pdl_electricite: missionData?.categories?.electricite?.data?.pdl || '',
    pce_gaz: missionData?.categories?.gaz?.data?.pce || '',
    // V5.44 : Champ notes pour informations complémentaires
    notes: missionData?.notes || ''
  };
  
  // V5.45 : Setter pour demenagementData avec first_name/last_name séparés
  const setDemenagementData = useCallback((updater) => {
    const newData = typeof updater === 'function' ? updater(demenagementData) : updater;
    updateMissionData({
      ...missionData,
      // V5.45 : FIL CONDUCTEUR - Stockage à la racine
      first_name: newData.first_name,
      last_name: newData.last_name,
      telephone: newData.telephone,
      email: newData.email,
      // V5.71 FIX : notes doit être propagé dans missionData
      notes: newData.notes,
      client: {
        first_name: newData.first_name,
        last_name: newData.last_name,
        nom_prenom: `${newData.first_name} ${newData.last_name}`.trim(),
        telephone: newData.telephone,
        email: newData.email
      },
      step1_adresses: {
        depart: {
          adresse: newData.ancienne_adresse,
          code_postal: newData.ancienne_code_postal,
          ville: newData.ancienne_ville
        },
        arrivee: {
          adresse: newData.nouvelle_adresse,
          code_postal: newData.nouvelle_code_postal,
          ville: newData.nouvelle_ville
        },
        date_demenagement: newData.date_demenagement
      }
    });
  }, [missionData, updateMissionData, demenagementData]);
  
  // V5.9 : État du Plan d'Action (conservé pour compatibilité)
  const [selectedActions, setSelectedActions] = useState([]);
  
  // V5.26 : Données de stratégie extraites de missionData
  const strategyData = missionData?.categories || {};
  const setStrategyData = useCallback((newStrategyData) => {
    // Convertir l'ancien format si nécessaire
    updateMissionData({
      ...missionData,
      categories: {
        ...missionData.categories,
        ...newStrategyData
      }
    });
  }, [missionData, updateMissionData]);
  
  // V5.26 : Données techniques et providers extraites de missionData
  const technicalData = {};
  const providerData = {};
  Object.entries(missionData?.categories || {}).forEach(([catId, catData]) => {
    if (catData?.provider) providerData[catId] = catData.provider;
    if (catData?.data) {
      Object.entries(catData.data).forEach(([key, value]) => {
        technicalData[`${catId}_${key}`] = value;
      });
    }
  });
  
  const setTechnicalData = useCallback(() => {}, []); // Géré via missionData
  const setProviderData = useCallback(() => {}, []); // Géré via missionData
  
  // État du formulaire Succession (V1.2 : défunt + mandant séparés)
  const [successionData, setSuccessionData] = useState({
    // Défunt
    prenom_defunt: '',
    nom_defunt: '',
    date_deces: '',
    lien_parente: '',
    // V1.2 : Mandant (l'ayant droit qui signe) — éditable, pré-rempli depuis le compte
    mandant_prenom: '',
    mandant_nom: '',
    mandant_telephone: '',
    mandant_email: '',
    // Legacy
    organismes: [],
    organismes_autres: '',
    rib_iban: ''
  });
  
  // Documents uploadés
  const [uploadedDocs, setUploadedDocs] = useState({
    justificatif_domicile: missionData?.justificatif_domicile || null,
    acte_deces: null,
    rib_beneficiaire: null,
    releves_bancaires: []
  });
  
  const [uploadProgress, setUploadProgress] = useState({});

  // V1.2 PAYWALL : état d'autorisation d'accès au tunnel pour ce mission_type
  const [paywallChecking, setPaywallChecking] = useState(true);

  const config = PORTAL_CONFIG[type] || PORTAL_CONFIG.audit;
  const Icon = config.icon;

  // Vérification auth
  useEffect(() => {
    const checkAuth = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await axios.get(`${API}/auth/me`, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        // V5.41 : Extraire l'objet user de la réponse
        setUser(res.data.user || res.data);
      } catch (error) {
        navigate('/login');
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  // V1.2 PAYWALL : garde d'accès au tunnel
  // 1. Si retour de Stripe (?session_id=...), finaliser le paiement via /payments/status
  // 2. Ensuite vérifier /missions/entitlements pour ce mission_type
  // 3. Si non payé -> redirection vers /dashboard?tab=payment avec message
  useEffect(() => {
    const enforcePaywall = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }
        const authHeaders = {
          headers: { Authorization: `Bearer ${token}` },
          withCredentials: true,
        };

        // Étape 1 : si on revient de Stripe avec session_id=..., déclencher la finalisation.
        const returnedSessionId = searchParams.get('session_id');
        const paymentFlag = searchParams.get('payment');
        if (returnedSessionId && paymentFlag === 'success') {
          try {
            await axios.get(
              `${API}/payments/status/${returnedSessionId}`,
              authHeaders
            );
            toast.success('Paiement confirmé — bienvenue dans votre mission');
          } catch (statusErr) {
            console.warn('Finalisation paiement (status) a échoué:', statusErr);
            // on ne bloque pas, le webhook peut finaliser
          }
        }

        // Étape 2 : vérifier les droits pour ce mission_type
        const entRes = await axios.get(
          `${API}/missions/entitlements`,
          authHeaders
        );
        const entitlements = entRes.data?.entitlements || {};
        const isAdmin = entRes.data?.is_admin === true;
        const hasAccess =
          isAdmin || entitlements[type]?.paid === true;

        if (!hasAccess) {
          toast.error(
            'Paiement requis pour accéder à cette mission. Choisissez votre pack.'
          );
          navigate('/dashboard?tab=payment');
          return;
        }
      } catch (err) {
        console.error('Paywall check error:', err);
        if (err.response?.status === 401) {
          navigate('/login');
          return;
        }
        toast.error('Impossible de vérifier votre accès — redirection');
        navigate('/dashboard?tab=payment');
        return;
      } finally {
        setPaywallChecking(false);
      }
    };
    enforcePaywall();
    // Ré-exécute si on change de type (ex: navigation entre silos)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);
  
  // V1.2 : AUTO-REMPLISSAGE SUCCESSION MANDANT depuis le compte (éditable ensuite)
  useEffect(() => {
    if (!user || type !== 'succession') return;
    // Ne re-remplit que si les champs mandant sont vides (pour ne pas écraser les modifs client)
    setSuccessionData((prev) => {
      if (prev.mandant_prenom || prev.mandant_nom) return prev;
      return {
        ...prev,
        mandant_prenom: user.first_name || (user.name ? user.name.split(' ')[0] : '') || '',
        mandant_nom: user.last_name || (user.name ? user.name.split(' ').slice(1).join(' ') : '') || '',
        mandant_telephone: user.phone || '',
        mandant_email: user.email || '',
      };
    });
  }, [user, type]);

  // V5.45 : AUTO-REMPLISSAGE - Pré-remplir avec first_name/last_name séparés + email
  // DDD.2 : sync aussi demenagementData (state local) quand l'auto-fill enrichit missionData
  // — sinon Step1Adresses affichait l'adresse persistée sans le nom/prénom du user connecté.
  useEffect(() => {
    if (!user) return;

    const fn = user.first_name || (user.name ? user.name.split(' ')[0] : '') || '';
    const ln = user.last_name || (user.name ? user.name.split(' ').slice(1).join(' ') : '') || '';
    const tel = user.phone || '';
    const em = user.email || '';

    // V5.45 : Vérifier si les champs sont vides
    const needsAutoFill = !missionData.first_name && !missionData.last_name && !missionData.telephone;

    if (needsAutoFill && (fn || ln || tel || em)) {
      const updatedMission = {
        ...missionData,
        first_name: fn,
        last_name: ln,
        telephone: tel,
        email: em,
        client: {
          ...missionData.client,
          first_name: fn,
          last_name: ln,
          nom_prenom: `${fn} ${ln}`.trim() || user.name || '',
          telephone: tel,
          email: em,
        },
      };
      updateMissionData(updatedMission);
      console.log(`✅ V5.45 Auto-remplissage missionData: ${fn} ${ln} | ${tel} | ${em}`);
    }

    // DDD.2 : sync demenagementData local (étape 1) si vide — étanchéité tunnel.
    setDemenagementData((prev) => {
      if (prev.first_name || prev.last_name || prev.telephone || prev.email) return prev;
      return {
        ...prev,
        first_name: fn,
        last_name: ln,
        telephone: tel,
        email: em,
      };
    });
  }, [user]);  // Ne se déclenche qu'une fois le user chargé
  
  // V5.5 : Récupérer les données ET l'étape courante depuis la BDD
  useEffect(() => {
    const fetchMissionData = async () => {
      try {
        const token = localStorage.getItem('token');
        
        // V5.20 : Si on a un ID de mission dans l'URL, récupérer les données de la BDD
        if (urlMissionId) {
          const missionRes = await axios.get(`${API}/missions/${urlMissionId}`, {
            headers: { 'Authorization': `Bearer ${token}` },
            withCredentials: true
          });
          
          if (missionRes.data.mission) {
            const mission = missionRes.data.mission;

            // V5.5 : Restaurer l'étape courante (priorité au champ details.current_step
            // car save-progress l'écrit là-bas, puis fallback mission.current_step).
            const restoredStep = mission?.details?.current_step
              || mission?.details?.mission_data?.current_step
              || mission.current_step;
            if (restoredStep && restoredStep > 1) {
              setCurrentStep(restoredStep);
              console.log(`✅ DDD.7 mission reprise à l'étape ${restoredStep}`);
            }

            // DDD.7 : Restaurer missionData unifié (format moderne — sauvegardé par save-progress)
            if (mission?.details?.mission_data && typeof mission.details.mission_data === 'object') {
              const md = mission.details.mission_data;
              if (md.id || md.client || md.step1_adresses || md.categories) {
                updateMissionData({ ...missionData, ...md, id: md.id || missionData?.id });
              }
            }
            // DDD.7 : Restaurer form_data (demenagementData / successionData) si sauvegardé
            if (mission?.details?.form_data && typeof mission.details.form_data === 'object') {
              const fd = mission.details.form_data;
              if (type === 'demenagement') {
                setDemenagementData(prev => ({ ...prev, ...fd }));
              } else if (type === 'succession' && typeof setSuccessionData === 'function') {
                try { setSuccessionData(prev => ({ ...prev, ...fd })); } catch (_) {}
              }
            }

            // Restaurer les données selon le type
            if (mission.details) {
              const details = mission.details;
              
              if (type === 'demenagement' && details.current_address) {
                setDemenagementData(prev => ({
                  ...prev,
                  ancienne_adresse: details.current_address?.address || '',
                  ancienne_ville: details.current_address?.city || '',
                  ancienne_code_postal: details.current_address?.postal_code || '',
                  nouvelle_adresse: details.new_address?.address || '',
                  nouvelle_ville: details.new_address?.city || '',
                  nouvelle_code_postal: details.new_address?.postal_code || '',
                  date_demenagement: details.moving_date || '',
                  contrats_transfert: details.contracts_to_transfer || [],
                  pdl_electricite: details.pdl_electricite || '',
                  pce_gaz: details.pce_gaz || ''
                }));
              }
              
              if (type === 'succession' && details.deceased) {
                setSuccessionData(prev => ({
                  ...prev,
                  nom_defunt: details.deceased?.name || '',
                  date_deces: details.deceased?.death_date || '',
                  lien_parente: details.family?.lien_parente || '',
                  organismes: details.organismes || [],
                  organismes_autres: details.organismes_autres || '',
                  rib_iban: details.family?.rib_iban || ''
                }));
              }
              
              if (details.uploaded_documents) {
                setUploadedDocs(prev => ({ ...prev, ...details.uploaded_documents }));
              }
            }
            
            return; // Exit si on a chargé depuis mission spécifique
          }
        }
        
        // Fallback : Récupérer depuis l'ancien endpoint /missions/details
        const res = await axios.get(`${API}/missions/details`, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        
        if (res.data.has_details && res.data.details) {
          const details = res.data.details;
          
          // V5.5 : Restaurer current_step si disponible
          if (details.current_step && details.current_step > 1) {
            setCurrentStep(details.current_step);
            console.log(`✅ Mission reprise à l'étape ${details.current_step}`);
          }
          
          // Restaurer les données déménagement si disponibles
          if (type === 'demenagement' && details.current_address) {
            setDemenagementData(prev => ({
              ...prev,
              ancienne_adresse: details.current_address?.address || '',
              ancienne_ville: details.current_address?.city || '',
              ancienne_code_postal: details.current_address?.postal_code || '',
              nouvelle_adresse: details.new_address?.address || '',
              nouvelle_ville: details.new_address?.city || '',
              nouvelle_code_postal: details.new_address?.postal_code || '',
              date_demenagement: details.moving_date || '',
              contrats_transfert: details.contracts_to_transfer || [],
              pdl_electricite: details.pdl_electricite || '',
              pce_gaz: details.pce_gaz || ''
            }));
          }
          
          // Restaurer les documents uploadés
          if (details.uploaded_documents) {
            setUploadedDocs(prev => ({ ...prev, ...details.uploaded_documents }));
          }
        }
      } catch (error) {
        console.log('Pas de données sauvegardées à restaurer');
      }
    };
    
    if (user) {
      fetchMissionData();
    }
  }, [user, type, urlMissionId]);
  
  // 🎫 TICKET #02 : Sauvegarde automatique en temps réel des données du tunnel
  // V5.41 : Ajout du mission_id pour isolation totale
  // V5.57 : Auto-save avec mission_data COMPLET pour synchronisation
  useEffect(() => {
    const autoSave = async () => {
      if (!user || type !== 'demenagement') return;
      
      // Ne sauvegarder que si au moins un champ est rempli
      const hasData = demenagementData.ancienne_adresse || 
                      demenagementData.nouvelle_adresse || 
                      demenagementData.date_demenagement;
      
      if (!hasData) return;
      
      try {
        const token = localStorage.getItem('token');
        // V5.57 : Envoyer TOUT missionData pour avoir step1_adresses en base
        await axios.post(`${API}/missions/auto-save`, {
          mission_type: 'demenagement',
          mission_id: localMissionId,  // V5.41 : Clé d'isolation unique
          form_data: demenagementData,
          mission_data: missionData  // V5.57 : FIX - Envoyer missionData complet !
        }, {
          headers: { 'Authorization': `Bearer ${token}` },
          withCredentials: true
        });
        console.log(`✅ V5.57 Auto-save: Mission ${localMissionId} synchronisée`);
      } catch (error) {
        // Silently fail - autosave shouldn't block the user
        console.log('Auto-save error:', error);
      }
    };
    
    // Debounce la sauvegarde (2 secondes après la dernière modification)
    const timeoutId = setTimeout(autoSave, 2000);
    return () => clearTimeout(timeoutId);
  }, [demenagementData, user, type, localMissionId, missionData]);
  
  // 🤖 PHASE D : Timer d'inactivité pour aide proactive (45s)
  const [proactiveMessage, setProactiveMessage] = useState('');
  
  // Fetch proactive message from API
  const fetchProactiveMessage = async (trigger) => {
    try {
      const res = await axios.get(`${API}/chatbot/proactive-message`, {
        params: { trigger, mission_type: type }
      });
      setProactiveMessage(res.data.message);
    } catch (error) {
      // Fallback messages
      const fallbacks = {
        demenagement: "Un doute sur votre numéro de compteur ou votre date de préavis ? Je connais les délais de tous les fournisseurs.",
        succession: "Je sais que cette période est difficile. Je suis là pour vous aider à lister les organismes sans rien oublier.",
        audit: "Besoin d'aide pour numériser vos documents ? Je peux vous expliquer comment faire avec votre smartphone."
      };
      setProactiveMessage(fallbacks[type] || "Besoin d'aide ?");
    }
  };
  
  useEffect(() => {
    if (currentStep >= 2) { // Sur les étapes d'upload
      const timer = setTimeout(() => {
        fetchProactiveMessage('inactivity');
        setShowHelper(true);
      }, 45000);
      setInactivityTimer(timer);
      
      return () => clearTimeout(timer);
    }
  }, [currentStep, type]);
  
  // Reset timer on user activity
  const resetInactivityTimer = () => {
    if (inactivityTimer) {
      clearTimeout(inactivityTimer);
    }
    setShowHelper(false);
    const timer = setTimeout(() => {
      fetchProactiveMessage('inactivity');
      setShowHelper(true);
    }, 45000);
    setInactivityTimer(timer);
  };
  
  // Upload de fichier
  const handleFileUpload = async (e, docType) => {
    resetInactivityTimer();
    const file = e.target.files[0];
    if (!file) return;
    
    const formData = new FormData();
    formData.append('file', file);
    formData.append('doc_type', docType);
    formData.append('mission_type', type);
    
    try {
      setUploadProgress(prev => ({ ...prev, [docType]: 0 }));
      const token = localStorage.getItem('token');
      
      const res = await axios.post(`${API}/documents/upload`, formData, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true,
        onUploadProgress: (progressEvent) => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          setUploadProgress(prev => ({ ...prev, [docType]: progress }));
        }
      });
      
      if (res.data.document_id) {
        setUploadedDocs(prev => ({
          ...prev,
          [docType]: {
            id: res.data.document_id,
            name: file.name,
            uploaded_at: new Date().toISOString()
          }
        }));
        toast.success(`Document "${file.name}" uploadé !`);
        setUploadProgress(prev => ({ ...prev, [docType]: 100 }));
      }
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'upload');
      setUploadProgress(prev => ({ ...prev, [docType]: 0 }));
    }
  };
  
  // V5.32 : Soumission du formulaire avec verrouillage VALIDATED
  // V5.33 FIX: Génération automatique du mandat et PDF lors de la validation
  const handleSubmit = async () => {
    setSubmitting(true);
    try {
      const token = localStorage.getItem('token');
      
      // V5.33 FIX: D'abord générer le mandat signé avec les PDF unitaires
      // Pour le déménagement, on crée le mandat automatiquement car l'étape 4 n'a pas de signature visuelle
      let mandatId = null;
      if (type === 'demenagement') {
        try {
          // Créer une signature placeholder (texte "Validation électronique")
          // Dans une vraie implémentation, on pourrait ajouter un canvas de signature
          const signaturePayload = {
            mission_type: type,
            mission_id: missionData?.id || urlMissionId,
            signature_data: '', // Signature vide = validation électronique
            form_data: {
              ...demenagementData,
              strategyChoices: strategyData,
              technicalData: technicalData
            },
            selected_actions: selectedActions,
            cgv_accepted: true,
            mandat_accepted: missionData?.consent_fee_disclaimer || false
          };
          
          const signResponse = await axios.post(`${API}/mission-mandats/sign`, signaturePayload, {
            headers: { 'Authorization': `Bearer ${token}` },
            withCredentials: true
          });
          
          mandatId = signResponse.data.mandat_id;
          console.log('✅ Mandat généré:', mandatId);
        } catch (signError) {
          console.error('Erreur génération mandat:', signError);
          // Continuer quand même avec la soumission
        }
      }
      
      // V5.32 : Préparer le payload avec le statut VALIDATED
      // V5.41 : Ajout du mission_id pour isolation totale
      const payload = {
        mission_type: type,
        mission_id: localMissionId,  // V5.41 : Clé d'isolation unique
        form_data: type === 'demenagement' ? {
          ...demenagementData,
          strategy_choices: strategyData,
          technical_data: technicalData,
          provider_data: providerData,
          selected_actions: selectedActions
        } : successionData,
        uploaded_documents: uploadedDocs,
        status: 'VALIDATED', // V5.32 : Verrouillage du statut
        mission_data: {
          ...missionData,
          mandat_id: mandatId // V5.33 FIX: Inclure le mandat_id
        }
      };
      
      await axios.post(`${API}/missions/portal-submit`, payload, {
        headers: { 'Authorization': `Bearer ${token}` },
        withCredentials: true
      });
      
      // V5.32 : Mettre à jour missionData avec le statut VALIDATED
      const validatedMissionData = {
        ...missionData,
        status: 'VALIDATED',
        mission_validated: true,
        validated_at: new Date().toISOString()
      };
      updateMissionData(validatedMissionData);
      
      // 🤖 PHASE D : Trigger 2 - Message de complétion
      try {
        const completionRes = await axios.get(`${API}/chatbot/proactive-message`, {
          params: { trigger: 'completion', mission_type: type }
        });
        toast.success(completionRes.data.message || '🎉 Mission validée ! Votre dossier est transmis à l\'Expert.');
      } catch {
        toast.success('🎉 Mission validée ! Votre dossier est transmis à l\'Expert.');
      }
      
      // V5.32 : Redirection vers "Gérer mon dossier" dans le Dashboard
      setTimeout(() => {
        navigate('/dashboard?tab=missions');
      }, 2000);
      
    } catch (error) {
      console.error('Submit error:', error);
      toast.error(error.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setSubmitting(false);
    }
  };
  
  // V5.5 : Fonction pour changer d'étape avec sauvegarde en BDD
  // V5.50 : Inclure mission_data complet pour synchronisation Admin
  const [stepSaving, setStepSaving] = useState(false);
  
  const handleStepChange = async (nextStep, currentStepData = null) => {
    // Empêcher les doubles clics
    if (stepSaving) return;
    
    setStepSaving(true);
    
    try {
      const token = localStorage.getItem('token');
      
      // V5.20 : Si on a un ID de mission dans l'URL, sauvegarder l'étape courante en BDD
      if ((urlMissionId || localMissionId) && currentStepData) {
        const missionIdToUse = urlMissionId || localMissionId;
        
        await axios.post(
          `${API}/missions/${missionIdToUse}/step/step_${currentStep}`,
          { 
            data: currentStepData, 
            completed: true 
          },
          {
            headers: { 'Authorization': `Bearer ${token}` },
            withCredentials: true
          }
        );
        console.log(`✅ Étape ${currentStep} sauvegardée`);
        
        // V5.50 : Auto-save complet avec mission_data pour synchronisation Admin
        await axios.post(
          `${API}/missions/auto-save`,
          {
            mission_type: type,
            mission_id: missionIdToUse,
            form_data: {
              ancienne_adresse: missionData?.step1_adresses?.depart?.adresse || missionData?.ancienne_adresse || '',
              ancienne_ville: missionData?.step1_adresses?.depart?.ville || missionData?.ancienne_ville || '',
              ancienne_code_postal: missionData?.step1_adresses?.depart?.code_postal || missionData?.ancienne_code_postal || '',
              nouvelle_adresse: missionData?.step1_adresses?.arrivee?.adresse || missionData?.nouvelle_adresse || '',
              nouvelle_ville: missionData?.step1_adresses?.arrivee?.ville || missionData?.nouvelle_ville || '',
              nouvelle_code_postal: missionData?.step1_adresses?.arrivee?.code_postal || missionData?.nouvelle_code_postal || '',
              date_demenagement: missionData?.step1_adresses?.date_demenagement || missionData?.date_demenagement || '',
              pdl_electricite: missionData?.categories?.electricite?.data?.pdl || '',
              pce_gaz: missionData?.categories?.gaz?.data?.pce || '',
              notes: missionData?.notes || ''
            },
            mission_data: missionData  // V5.50 : Envoyer mission_data complet
          },
          {
            headers: { 'Authorization': `Bearer ${token}` },
            withCredentials: true
          }
        );
        console.log(`✅ V5.50 Mission data synchronized`);
      }
      
      // Passer à l'étape suivante
      updateCurrentStepInMission(nextStep);
      
    } catch (error) {
      console.error('Erreur sauvegarde étape:', error);
      // Continuer quand même vers l'étape suivante
      updateCurrentStepInMission(nextStep);
    } finally {
      setStepSaving(false);
    }
  };
  
  // V5.5 : Aller directement au mandat si questionnaire terminé
  const goToMandat = () => {
    if (currentStep < 4) {
      setCurrentStep(4);
    }
  };
  
  // Calcul du pourcentage de complétion
  const getCompletionPercent = () => {
    if (type === 'demenagement') {
      let filled = 0;
      if (demenagementData.ancienne_adresse) filled++;
      if (demenagementData.nouvelle_adresse) filled++;
      if (demenagementData.date_demenagement) filled++;
      if (demenagementData.contrats_transfert.length > 0) filled++;
      if (uploadedDocs.justificatif_domicile || uploadedDocs.releves_bancaires.length > 0) filled++;
      return Math.round((filled / 5) * 100);
    } else if (type === 'succession') {
      let filled = 0;
      if (successionData.nom_defunt) filled++;
      if (successionData.date_deces) filled++;
      if (successionData.organismes.length > 0) filled++;
      if (uploadedDocs.acte_deces) filled += 2; // Poids double car obligatoire
      return Math.round((filled / 5) * 100);
    }
    return 0;
  };
  
  // V5 REFONDATION : Calculer les étapes complétées
  const getCompletedSteps = () => {
    const completed = [];
    if (type === 'demenagement') {
      if (demenagementData.ancienne_adresse && demenagementData.nouvelle_adresse && demenagementData.date_demenagement) {
        completed.push('infos');
      }
      if (demenagementData.contrats_transfert.length > 0) {
        completed.push('contrats');
      }
      if (uploadedDocs.justificatif_domicile) {
        completed.push('documents');
      }
    } else if (type === 'succession') {
      if (successionData.nom_defunt && successionData.date_deces && successionData.lien_parente) {
        completed.push('organismes');
      }
      if (uploadedDocs.acte_deces) {
        completed.push('documents');
      }
    }
    return completed;
  };
  
  // Mobile menu state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  
  // V1.2 PAYWALL : on attend aussi la vérification des droits avant d'afficher le tunnel
  if (loading || paywallChecking) {
    return (
      <div className="min-h-screen bg-void flex items-center justify-center">
        <Loader2 className="w-8 h-8 text-cyan-400 animate-spin" />
      </div>
    );
  }
  
  return (
    <div className="min-h-screen bg-void flex">
      {/* V5 REFONDATION : Sidebar Contextuelle Desktop - Affiche les étapes de la mission */}
      <ContextualSidebar 
        context="mission"
        missionType={type}
        currentStep={currentStep}
        completedSteps={getCompletedSteps()}
        onStepClick={(step) => setCurrentStep(step)}
        user={user}
        onLogout={() => {
          localStorage.removeItem('token');
          navigate('/login');
        }}
      />
      
      {/* V5 REFONDATION : Menu Mobile */}
      <MobileMenu 
        isOpen={mobileMenuOpen}
        setIsOpen={setMobileMenuOpen}
        context="mission"
        missionType={type}
        currentStep={currentStep}
        completedSteps={getCompletedSteps()}
        user={user}
        onLogout={() => {
          localStorage.removeItem('token');
          navigate('/login');
        }}
      />
      
      {/* Contenu Principal */}
      <div className="flex-1 flex flex-col">
        {/* V5 REFONDATION : Stepper Mobile (horizontal, discret) */}
        <div className="md:hidden">
          <MobileMissionStepper 
            missionType={type}
            currentStep={currentStep}
            completedSteps={getCompletedSteps()}
          />
        </div>
        
        {/* Header Desktop */}
        <header className="hidden md:block border-b border-gray-800 bg-void/80 backdrop-blur-md sticky top-0 z-30">
          <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between gap-4">
            <div className="flex items-center gap-3 min-w-0">
              <div className={`w-10 h-10 rounded-lg bg-${config.color === 'success' ? 'success' : config.color}-400/20 flex items-center justify-center flex-shrink-0`}>
                <Icon className={`w-5 h-5 text-${config.color === 'success' ? 'success' : config.color}-400`} />
              </div>
              <div className="min-w-0">
                <h1 className="font-orbitron text-lg font-bold text-white truncate">{config.title}</h1>
                <p className="text-gray-500 text-xs truncate">{config.subtitle}</p>
              </div>
            </div>
            <div className="flex items-center gap-3 flex-shrink-0">
              {/* DDD.7 : bouton Sauvegarder & quitter — visible à toutes les étapes du tunnel */}
              <button
                onClick={handleSaveAndExit}
                disabled={savingProgress}
                className="flex items-center gap-2 px-3 py-1.5 rounded-lg border border-cyan-400/40 bg-cyan-400/5 text-cyan-300 hover:bg-cyan-400/15 hover:text-cyan-200 transition-colors text-xs font-mono disabled:opacity-50"
                data-testid="save-and-exit-btn"
              >
                {savingProgress ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                Sauvegarder & reprendre plus tard
              </button>
              <div className={`px-3 py-1 rounded-full bg-${config.color === 'success' ? 'success' : config.color}-400/20 text-${config.color === 'success' ? 'success' : config.color}-400 text-sm font-mono`}>
                {getCompletionPercent()}% complété
              </div>
            </div>
          </div>
        </header>

        {/* Mobile Header avec Burger */}
        <header className="md:hidden flex items-center justify-between px-4 py-3 border-b border-gray-800">
          <button
            onClick={() => setMobileMenuOpen(true)}
            className="p-2 text-gray-400 hover:text-white"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="font-orbitron text-sm font-bold text-cyan-400 truncate">{config.title}</h1>
          <div className="flex items-center gap-2">
            {/* DDD.7 mobile : icône save compacte */}
            <button
              onClick={handleSaveAndExit}
              disabled={savingProgress}
              aria-label="Sauvegarder et reprendre plus tard"
              className="p-1.5 rounded-md border border-cyan-400/30 bg-cyan-400/5 text-cyan-300 hover:bg-cyan-400/10 disabled:opacity-50"
              data-testid="save-and-exit-btn-mobile"
            >
              {savingProgress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            </button>
            <span className={`text-${config.color === 'success' ? 'success' : config.color}-400 text-xs font-mono`}>
              {getCompletionPercent()}%
            </span>
          </div>
        </header>

        {/* DDD.8 : bandeau persistance + GROS CTA Sauvegarder bien visible */}
        <div className="max-w-4xl mx-auto w-full px-4 pt-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 p-4 rounded-xl bg-gradient-to-r from-cyan-400/10 to-magenta-400/10 border border-cyan-400/30">
            <div className="flex items-start gap-3 flex-1 min-w-0">
              <div className="w-9 h-9 rounded-lg bg-cyan-400/20 border border-cyan-400/40 flex items-center justify-center flex-shrink-0">
                <Save className="w-4 h-4 text-cyan-300" />
              </div>
              <div className="min-w-0">
                <p className="text-white text-sm font-bold mb-0.5">Pas tout sous la main ?</p>
                <p className="text-gray-300 text-xs leading-snug">
                  Sauvegarde tout en un clic, <span className="text-cyan-300 font-medium">tu reprendras où tu t'es arrêté</span>{' '}
                  depuis <span className="text-magenta-300 font-medium">Mes Missions</span>.
                </p>
              </div>
            </div>
            <button
              onClick={handleSaveAndExit}
              disabled={savingProgress}
              className="flex-shrink-0 inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-lg bg-cyan-400 text-black font-bold text-sm shadow-lg shadow-cyan-400/30 hover:bg-cyan-300 hover:shadow-cyan-400/50 disabled:opacity-50 transition-all"
              data-testid="save-and-exit-cta"
            >
              {savingProgress ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Sauvegarder & reprendre plus tard
            </button>
          </div>
        </div>
        
        {/* Progress Bar (visible sur tous les écrans) */}
        <div className="max-w-4xl mx-auto w-full px-4 py-4">
          <div className="h-1 bg-gray-800 rounded-full overflow-hidden">
            <motion.div 
              className={`h-full bg-gradient-to-r ${config.bgGradient}`}
              initial={{ width: 0 }}
              animate={{ width: `${getCompletionPercent()}%` }}
              transition={{ duration: 0.5 }}
            />
          </div>
        </div>
      
        {/* Main Content */}
        <main className="max-w-4xl mx-auto px-4 pb-12">
          <AnimatePresence mode="wait">
            {/* DÉMÉNAGEMENT - Étape 1 : Adresses */}
            {type === 'demenagement' && currentStep === 1 && (
              <motion.div
                key="demenagement-step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                className="space-y-6"
              >
                <div className="glass-card p-6 border border-cyan-400/30">
                  <h2 className="font-orbitron text-xl font-bold text-white mb-6 flex items-center gap-2">
                    <MapPin className="w-5 h-5 text-cyan-400" />
                    Vos Adresses
                  </h2>
                  
                  {/* V5.45 : Coordonnées du mandant - Prénom/Nom/Téléphone séparés */}
                  <div className="mb-6 p-4 bg-cyan-400/5 rounded-lg border border-cyan-400/20">
                    <h3 className="text-cyan-400 font-mono text-sm border-b border-cyan-400/30 pb-2 mb-4">
                      // VOS_COORDONNÉES
                    </h3>
                    <div className="grid md:grid-cols-3 gap-4">
                      <Input
                        placeholder="Prénom *"
                        value={demenagementData.first_name || ''}
                        onChange={(e) => setDemenagementData(prev => ({ ...prev, first_name: e.target.value }))}
                        className="bg-gray-800/50 border-gray-700"
                        autoComplete="given-name"
                        data-testid="first-name-input"
                      />
                      <Input
                        placeholder="Nom *"
                        value={demenagementData.last_name || ''}
                        onChange={(e) => setDemenagementData(prev => ({ ...prev, last_name: e.target.value }))}
                        className="bg-gray-800/50 border-gray-700"
                        autoComplete="family-name"
                        data-testid="last-name-input"
                      />
                      <Input
                        placeholder="Téléphone *"
                        value={demenagementData.telephone || ''}
                        onChange={(e) => setDemenagementData(prev => ({ ...prev, telephone: e.target.value }))}
                        className="bg-gray-800/50 border-gray-700"
                        autoComplete="tel"
                        data-testid="telephone-input"
                      />
                    </div>
                    {/* V5.45 : Email en lecture seule */}
                    {demenagementData.email && (
                      <div className="mt-3">
                        <Input
                          placeholder="Email"
                          value={demenagementData.email}
                          disabled
                          className="bg-gray-800/30 border-gray-700 text-gray-400 cursor-not-allowed"
                          data-testid="email-readonly"
                        />
                        <p className="text-gray-500 text-xs mt-1">Email du compte (non modifiable ici)</p>
                      </div>
                    )}
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Ancienne adresse — V1.2 : autocomplete API gouv */}
                    <div className="space-y-4">
                      <h3 className="text-gray-400 font-mono text-sm border-b border-gray-700 pb-2">
                        // ANCIENNE_ADRESSE
                      </h3>
                      <AddressAutocomplete
                        value={{
                          adresse: demenagementData.ancienne_adresse,
                          code_postal: demenagementData.ancienne_code_postal,
                          ville: demenagementData.ancienne_ville,
                        }}
                        onChange={(sug) => setDemenagementData(prev => ({
                          ...prev,
                          ancienne_adresse: sug.adresse || '',
                          ancienne_code_postal: sug.code_postal || prev.ancienne_code_postal,
                          ancienne_ville: sug.ville || prev.ancienne_ville,
                        }))}
                        placeholder="Adresse de départ (ex: 8 rue Toul...)"
                        accentClass="focus:border-red-400"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Code postal"
                          value={demenagementData.ancienne_code_postal}
                          onChange={(e) => setDemenagementData(prev => ({ ...prev, ancienne_code_postal: e.target.value }))}
                          className="bg-gray-800/50 border-gray-700"
                          autoComplete="off"
                          data-testid="ancienne-cp-input"
                        />
                        <Input
                          placeholder="Ville"
                          value={demenagementData.ancienne_ville}
                          onChange={(e) => setDemenagementData(prev => ({ ...prev, ancienne_ville: e.target.value }))}
                          className="bg-gray-800/50 border-gray-700"
                          autoComplete="off"
                          data-testid="ancienne-ville-input"
                        />
                      </div>
                    </div>

                    {/* Nouvelle adresse — V1.2 : autocomplete API gouv */}
                    <div className="space-y-4">
                      <h3 className="text-cyan-400 font-mono text-sm border-b border-cyan-400/30 pb-2">
                        // NOUVELLE_ADRESSE
                      </h3>
                      <AddressAutocomplete
                        value={{
                          adresse: demenagementData.nouvelle_adresse,
                          code_postal: demenagementData.nouvelle_code_postal,
                          ville: demenagementData.nouvelle_ville,
                        }}
                        onChange={(sug) => setDemenagementData(prev => ({
                          ...prev,
                          nouvelle_adresse: sug.adresse || '',
                          nouvelle_code_postal: sug.code_postal || prev.nouvelle_code_postal,
                          nouvelle_ville: sug.ville || prev.nouvelle_ville,
                        }))}
                        placeholder="Adresse d'arrivée (ex: 4 chem Blagnac...)"
                        accentClass="focus:border-cyan-400"
                      />
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          placeholder="Code postal"
                          value={demenagementData.nouvelle_code_postal}
                          onChange={(e) => setDemenagementData(prev => ({ ...prev, nouvelle_code_postal: e.target.value }))}
                          className="bg-gray-800/50 border-cyan-400/30"
                          autoComplete="off"
                          data-testid="nouvelle-cp-input"
                        />
                        <Input
                          placeholder="Ville"
                          value={demenagementData.nouvelle_ville}
                          onChange={(e) => setDemenagementData(prev => ({ ...prev, nouvelle_ville: e.target.value }))}
                          className="bg-gray-800/50 border-cyan-400/30"
                          autoComplete="off"
                          data-testid="nouvelle-ville-input"
                        />
                      </div>
                    </div>
                  </div>
                
                {/* Date de déménagement */}
                <div className="mt-6">
                  <label className="text-gray-400 text-sm font-mono mb-2 block">
                    <Calendar className="w-4 h-4 inline mr-2" />
                    Date du déménagement <span className="text-red-400">*</span>
                  </label>
                  <Input
                    type="date"
                    value={demenagementData.date_demenagement}
                    onChange={(e) => setDemenagementData(prev => ({ ...prev, date_demenagement: e.target.value }))}
                    // V1.2 : validation cohérente — min = aujourd'hui, max = dans 6 mois
                    min={new Date().toISOString().split('T')[0]}
                    max={new Date(Date.now() + 6 * 30 * 24 * 3600 * 1000).toISOString().split('T')[0]}
                    className="bg-gray-800/50 border-cyan-400/40 hover:border-cyan-400 focus:border-cyan-400 max-w-xs text-white"
                    style={{ colorScheme: 'dark' }}
                    required
                  />
                  <p className="text-gray-500 text-xs mt-1 font-mono">
                    Date dans les 6 prochains mois — avant cette date notre expert déclenche les démarches.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button 
                  onClick={() => {
                    // V5.50 : Sauvegarde complète des adresses dans mission_data.step1_adresses
                    const updatedMissionData = {
                      ...missionData,
                      step1_adresses: {
                        depart: {
                          adresse: demenagementData.ancienne_adresse,
                          code_postal: demenagementData.ancienne_code_postal,
                          ville: demenagementData.ancienne_ville
                        },
                        arrivee: {
                          adresse: demenagementData.nouvelle_adresse,
                          code_postal: demenagementData.nouvelle_code_postal,
                          ville: demenagementData.nouvelle_ville
                        },
                        date_demenagement: demenagementData.date_demenagement
                      },
                      // V5.50 : Dupliquer à la racine pour compatibilité
                      ancienne_adresse: demenagementData.ancienne_adresse,
                      ancienne_code_postal: demenagementData.ancienne_code_postal,
                      ancienne_ville: demenagementData.ancienne_ville,
                      nouvelle_adresse: demenagementData.nouvelle_adresse,
                      nouvelle_code_postal: demenagementData.nouvelle_code_postal,
                      nouvelle_ville: demenagementData.nouvelle_ville,
                      date_demenagement: demenagementData.date_demenagement
                    };
                    updateMissionData(updatedMissionData);
                    handleStepChange(2, updatedMissionData.step1_adresses);
                  }}
                  disabled={!demenagementData.first_name || !demenagementData.last_name || !demenagementData.telephone || !demenagementData.ancienne_adresse || !demenagementData.nouvelle_adresse || !demenagementData.date_demenagement || stepSaving}
                  className="bg-cyan-400 text-black hover:bg-cyan-300 font-orbitron"
                >
                  {stepSaving ? 'Sauvegarde...' : 'Continuer'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              {/* V5.45 : Message d'aide avec tous les champs obligatoires */}
              {(!demenagementData.first_name || !demenagementData.last_name || !demenagementData.telephone || !demenagementData.ancienne_adresse || !demenagementData.nouvelle_adresse || !demenagementData.date_demenagement) && (
                <p className="text-orange-400 text-xs flex items-center gap-1 mt-2">
                  <AlertCircle className="w-4 h-4" />
                  Prénom, Nom, Téléphone, Adresses et Date sont obligatoires
                </p>
              )}
            </motion.div>
          )}
          
          {/* V5.26 DÉMÉNAGEMENT - Étape 2 : Stratégie de Gestion avec architecture silos */}
          {type === 'demenagement' && currentStep === 2 && (
            <motion.div
              key="demenagement-step2-strategy-v526"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <StrategyStepV526
                missionData={missionData}
                onMissionDataChange={updateMissionData}
                onNext={() => {
                  // V5.26 : Préparer les actions sélectionnées pour le mandat
                  const actions = [];
                  Object.entries(missionData?.categories || {}).forEach(([catId, catData]) => {
                    if (catData?.status && catData.status !== 'none') {
                      actions.push({
                        category: catId,
                        action: catData.status === 'active' ? 'transfer' : 'liquidate',
                        label: catData.status === 'active' ? 'J\'active' : 'Je désactive',
                        category_title: catId.charAt(0).toUpperCase() + catId.slice(1),
                        provider: catData.provider || ''
                      });
                    }
                  });
                  setSelectedActions(actions);
                  updateCurrentStepInMission(3);
                }}
                onBack={() => updateCurrentStepInMission(1)}
              />
            </motion.div>
          )}
          
          {/* V5.26 DÉMÉNAGEMENT - Étape 3 : Collecte Technique avec Filtre Actif */}
          {type === 'demenagement' && currentStep === 3 && (
            <motion.div
              key="demenagement-step3-technical-v526"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
            >
              <TechnicalCollectionStepV526
                missionData={missionData}
                onMissionDataChange={updateMissionData}
                onNext={() => {
                  // V5.31 : Sauter directement à l'étape 4 (Synthèse)
                  updateCurrentStepInMission(4);
                }}
                onBack={() => updateCurrentStepInMission(2)}
              />
            </motion.div>
          )}
          
          {/* V5.31 DÉMÉNAGEMENT - Étape 4 : SYNTHÈSE CONSOLIDÉE (Signature + Récapitulatif) */}
          {type === 'demenagement' && currentStep === 4 && (
            (() => {
              // V5.31 : Vérification que les données minimales sont présentes
              const hasMinimalData = demenagementData?.ancienne_adresse && 
                                     demenagementData?.nouvelle_adresse && 
                                     demenagementData?.date_demenagement;
              
              if (!hasMinimalData) {
                return (
                  <motion.div
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    className="glass-card p-6 border border-red-500/30 bg-red-500/5 text-center"
                  >
                    <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
                    <h3 className="text-white font-bold mb-2">Données incomplètes</h3>
                    <p className="text-gray-400 mb-4">
                      Certaines informations sont manquantes pour générer la synthèse.
                      Veuillez compléter les étapes précédentes.
                    </p>
                    <Button onClick={() => updateCurrentStepInMission(1)} className="bg-cyan-500 text-black">
                      Retour à l'étape 1
                    </Button>
                  </motion.div>
                );
              }
              
              return (
                <motion.div
                  key="demenagement-step4-synthese-v531"
                  initial={{ opacity: 0, x: 20 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -20 }}
                  className="space-y-6"
                >
                  {/* V5.69 : En-tête premium */}
                  <div className="text-center mb-8">
                    <div className="inline-flex items-center gap-2 px-5 py-2 rounded-full bg-cyan-400/10 border border-cyan-400/30 mb-4">
                      <Sparkles className="w-4 h-4 text-cyan-400" />
                      <span className="text-cyan-400 text-sm font-mono font-bold tracking-widest">ÉTAPE FINALE</span>
                    </div>
                    <h2 className="text-3xl font-orbitron font-bold text-white mb-2">Votre dossier est prêt</h2>
                    <p className="text-gray-400">Vérifiez vos informations puis signez le mandat pour lancer votre mission</p>
                  </div>
                  
                  {/* V5.69 : Adresses — cartes premium */}
                  <div className="space-y-3">
                    <h3 className="font-orbitron text-sm text-gray-500 tracking-widest px-1">// LOGISTIQUE</h3>
                    <div className="grid md:grid-cols-2 gap-4">
                      {/* Ancienne adresse */}
                      <div className="relative overflow-hidden rounded-xl border border-red-400/30 bg-red-400/5 p-5">
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-red-400 to-red-400/20 rounded-l-xl" />
                        <p className="text-red-400 text-xs font-mono mb-3 tracking-widest">DÉPART</p>
                        <p className="text-white text-lg font-medium leading-tight">{demenagementData.ancienne_adresse}</p>
                        <p className="text-gray-400 mt-1">{demenagementData.ancienne_code_postal} {demenagementData.ancienne_ville}</p>
                      </div>
                      {/* Nouvelle adresse */}
                      <div className="relative overflow-hidden rounded-xl border border-cyan-400/30 bg-cyan-400/5 p-5">
                        <div className="absolute top-0 left-0 w-1 h-full bg-gradient-to-b from-cyan-400 to-cyan-400/20 rounded-l-xl" />
                        <p className="text-cyan-400 text-xs font-mono mb-3 tracking-widest">ARRIVÉE</p>
                        <p className="text-white text-lg font-medium leading-tight">{demenagementData.nouvelle_adresse}</p>
                        <p className="text-gray-400 mt-1">{demenagementData.nouvelle_code_postal} {demenagementData.nouvelle_ville}</p>
                      </div>
                    </div>
                    {/* Date */}
                    <div className="flex items-center gap-3 px-5 py-3 rounded-xl bg-gray-800/40 border border-gray-700/50">
                      <Calendar className="w-4 h-4 text-gray-500 flex-shrink-0" />
                      <div>
                        <p className="text-gray-500 text-xs font-mono">DATE DU DÉMÉNAGEMENT</p>
                        <p className="text-white font-mono">
                          {demenagementData.date_demenagement ? new Date(demenagementData.date_demenagement).toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' }) : 'Non spécifiée'}
                        </p>
                      </div>
                    </div>
                  </div>
                  
                  {/* V1.2 PREMIUM : Contrats concernés — carte unique compacte (même style que dashboard) */}
                  {selectedActions && selectedActions.length > 0 && (() => {
                    const transfers = selectedActions.filter(a => a.action === 'transfer');
                    const liquidates = selectedActions.filter(a => a.action === 'liquidate');
                    return (
                      <div className="glass-card p-6 border border-gray-700 relative overflow-hidden">
                        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-emerald-400 via-gray-600 to-red-400" />
                        <div className="flex items-center gap-3 mb-5">
                          <div className="w-10 h-10 rounded-xl bg-gray-800 border border-gray-700 flex items-center justify-center">
                            <FileText className="w-5 h-5 text-gray-400" />
                          </div>
                          <div className="flex-1">
                            <h3 className="font-orbitron text-base font-bold text-white">Contrats concernés</h3>
                            <p className="text-gray-500 text-[10px] font-mono tracking-widest">// {selectedActions.length} fournisseur(s) à traiter</p>
                          </div>
                          <div className="flex items-center gap-3 text-xs font-mono">
                            {transfers.length > 0 && (
                              <span className="flex items-center gap-1.5 text-emerald-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" /> {transfers.length} transfert{transfers.length > 1 ? 's' : ''}
                              </span>
                            )}
                            {liquidates.length > 0 && (
                              <span className="flex items-center gap-1.5 text-red-400">
                                <span className="w-1.5 h-1.5 rounded-full bg-red-400" /> {liquidates.length} résiliation{liquidates.length > 1 ? 's' : ''}
                              </span>
                            )}
                          </div>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                          {transfers.length > 0 && (
                            <div className="rounded-lg border-l-2 border-emerald-400/60 bg-emerald-400/5 px-4 py-3">
                              <p className="text-emerald-400 text-[10px] font-mono tracking-widest mb-2 flex items-center gap-1.5">
                                <CheckCircle className="w-3 h-3" /> TRANSFERT
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {transfers.map((action, idx) => (
                                  <span key={idx} className="px-2.5 py-1 rounded-md bg-emerald-400/10 border border-emerald-400/20 text-emerald-300 text-xs font-medium">
                                    {action.provider || action.category_title}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                          {liquidates.length > 0 && (
                            <div className="rounded-lg border-l-2 border-red-400/60 bg-red-400/5 px-4 py-3">
                              <p className="text-red-400 text-[10px] font-mono tracking-widest mb-2 flex items-center gap-1.5">
                                <span className="text-base leading-none">✗</span> RÉSILIATION
                              </p>
                              <div className="flex flex-wrap gap-1.5">
                                {liquidates.map((action, idx) => (
                                  <span key={idx} className="px-2.5 py-1 rounded-md bg-red-400/10 border border-red-400/20 text-red-300 text-xs font-medium">
                                    {action.provider || action.category_title}
                                  </span>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })()}
                  
                  {/* V5.66 : Champ Notes déplacé ici (étape 4 Synthèse) */}
                  <div className="glass-card p-6 border border-purple-400/30 bg-purple-400/5">
                    <h3 className="font-orbitron text-lg font-bold text-white mb-2">Informations Complémentaires</h3>
                    <p className="text-gray-400 text-sm mb-4">
                      Précisions sur vos contrats, numéros de référence, demandes spéciales pour l'expert...
                    </p>
                    <textarea
                      value={demenagementData?.notes || ''}
                      onChange={(e) => setDemenagementData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Ex: Mon compteur électrique est au nom de mon ex-conjoint, je souhaite le mettre à mon nom..."
                      rows={4}
                      className="w-full px-4 py-3 bg-gray-900 border border-gray-700 rounded-lg text-white placeholder-gray-500 focus:border-purple-400 focus:ring-1 focus:ring-purple-400 transition-colors resize-none"
                      data-testid="notes-textarea-synthese"
                    />
                  </div>

                  {/* V5.34 : SIGNATURE DU MANDAT - Canvas de signature */}
                  <MandatSignature
                    missionType="demenagement"
                    missionId={missionData?.id || urlMissionId}
                    userData={user}
                    formData={{
                      ...demenagementData,
                      // V5.34 : Envoyer les noms des organismes uniquement (pas les références)
                      contrats_transfert: selectedActions?.filter(a => a.action === 'transfer').map(a => a.provider || a.category_title) || [],
                      contrats_resiliation: selectedActions?.filter(a => a.action === 'liquidate').map(a => a.provider || a.category_title) || []
                    }}
                    selectedActions={selectedActions}
                    onSigned={async (signResult) => {
                      // V5.34 : Après signature réussie, sauvegarder le mandat_id et soumettre
                      console.log('✅ Mandat signé:', signResult);
                      
                      // Mettre à jour les données avec le mandat_id
                      const updatedMissionData = {
                        ...missionData,
                        mandat_id: signResult.mandat_id,
                        consent_fee_disclaimer: true,
                        signed_at: new Date().toISOString()
                      };
                      updateMissionData(updatedMissionData);
                      
                      // Soumettre automatiquement après signature
                      setSubmitting(true);
                      try {
                        const token = localStorage.getItem('token');
                        const payload = {
                          mission_type: type,
                          // V5.67 : Priorité à l'ID URL (source de vérité) puis localStorage
                          mission_id: urlMissionId || localMissionId,
                          form_data: {
                            ...demenagementData,
                            strategy_choices: strategyData,
                            technical_data: technicalData,
                            provider_data: providerData,
                            selected_actions: selectedActions
                          },
                          uploaded_documents: uploadedDocs,
                          status: 'VALIDATED',
                          mission_data: {
                            ...updatedMissionData,
                            mandat_id: signResult.mandat_id
                          }
                        };
                        
                        const response = await axios.post(`${API}/missions/portal-submit`, payload, {
                          headers: { 'Authorization': `Bearer ${token}` },
                          withCredentials: true
                        });
                        
                        toast.success('🎉 Mission validée avec succès !');
                        
                        // V5.35 FIX: Utiliser le mission_id retourné par l'API
                        const returnedMissionId = response.data.mission_id || missionData?.id || urlMissionId;
                        
                        // Rediriger vers la page de gestion
                        setTimeout(() => {
                          navigate(`/missions/manage?mission=${returnedMissionId}`);
                        }, 1500);
                      } catch (err) {
                        console.error('Erreur soumission:', err);
                        toast.error('Erreur lors de la validation');
                      } finally {
                        setSubmitting(false);
                      }
                    }}
                    onBack={() => updateCurrentStepInMission(3)}
                  />
                </motion.div>
              );
            })()
          )}
          
          {/* SUCCESSION - Étape 1 : Organismes */}
          {type === 'succession' && currentStep === 1 && (
            <motion.div
              key="succession-step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              {/* V1.2 : Bloc MANDANT — le client qui signe (souvent ≠ le défunt). Éditable. */}
              <div className="glass-card p-6 border border-cyan-400/30 mb-6">
                <h2 className="font-orbitron text-xl font-bold text-white mb-2 flex items-center gap-2">
                  <User className="w-5 h-5 text-cyan-400" />
                  Vos coordonnées (mandant)
                </h2>
                <p className="text-gray-500 text-xs mb-4 font-mono">
                  // Vous (l'ayant droit qui signe le mandat). Pré-rempli, éditable.
                </p>
                <div className="grid md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">Votre prénom <span className="text-red-400">*</span></label>
                    <Input
                      placeholder="Ex: Marie"
                      value={successionData.mandant_prenom || ''}
                      onChange={(e) => setSuccessionData(prev => ({ ...prev, mandant_prenom: e.target.value }))}
                      className="bg-gray-800/50 border-gray-700 focus:border-cyan-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">Votre nom <span className="text-red-400">*</span></label>
                    <Input
                      placeholder="Ex: Dupont"
                      value={successionData.mandant_nom || ''}
                      onChange={(e) => setSuccessionData(prev => ({ ...prev, mandant_nom: e.target.value }))}
                      className="bg-gray-800/50 border-gray-700 focus:border-cyan-400"
                      required
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">Votre téléphone <span className="text-red-400">*</span></label>
                    <Input
                      type="tel"
                      placeholder="06 12 34 56 78"
                      value={successionData.mandant_telephone || ''}
                      onChange={(e) => setSuccessionData(prev => ({ ...prev, mandant_telephone: e.target.value }))}
                      className="bg-gray-800/50 border-gray-700 focus:border-cyan-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">Votre email <span className="text-red-400">*</span></label>
                    <Input
                      type="email"
                      placeholder="vous@exemple.fr"
                      value={successionData.mandant_email || ''}
                      onChange={(e) => setSuccessionData(prev => ({ ...prev, mandant_email: e.target.value }))}
                      className="bg-gray-800/50 border-gray-700 focus:border-cyan-400"
                      required
                    />
                  </div>
                </div>
              </div>

              <div className="glass-card p-6 border border-magenta-400/30">
                <h2 className="font-orbitron text-xl font-bold text-white mb-4 flex items-center gap-2">
                  <Building2 className="w-5 h-5 text-magenta-400" />
                  Informations sur le défunt
                </h2>

                {/* V1.2 : Prénom + Nom séparés pour conformité légale mandat */}
                <div className="grid md:grid-cols-2 gap-4 mb-6">
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">Prénom du défunt <span className="text-red-400">*</span></label>
                    <Input
                      placeholder="Ex: Jean"
                      value={successionData.prenom_defunt || ''}
                      onChange={(e) => setSuccessionData(prev => ({ ...prev, prenom_defunt: e.target.value }))}
                      className="bg-gray-800/50 border-gray-700 focus:border-magenta-400"
                      required
                    />
                  </div>
                  <div>
                    <label className="text-gray-400 text-xs mb-1 block">Nom du défunt <span className="text-red-400">*</span></label>
                    <Input
                      placeholder="Ex: Dupont"
                      value={successionData.nom_defunt}
                      onChange={(e) => setSuccessionData(prev => ({ ...prev, nom_defunt: e.target.value }))}
                      className="bg-gray-800/50 border-gray-700 focus:border-magenta-400"
                      required
                    />
                  </div>
                </div>

                <div className="mb-6">
                  <label className="text-gray-400 text-xs mb-1 block">Date du décès <span className="text-red-400">*</span></label>
                  <Input
                    type="date"
                    value={successionData.date_deces}
                    onChange={(e) => setSuccessionData(prev => ({ ...prev, date_deces: e.target.value }))}
                    // V1.2 : validation — date dans le passé (max = aujourd'hui) et dans l'année écoulée
                    min={new Date(Date.now() - 365 * 24 * 3600 * 1000).toISOString().split('T')[0]}
                    max={new Date().toISOString().split('T')[0]}
                    className="bg-gray-800/50 border-magenta-400/40 hover:border-magenta-400 focus:border-magenta-400 text-white"
                    style={{ colorScheme: 'dark' }}
                    required
                  />
                  <p className="text-gray-500 text-xs mt-1 font-mono">
                    Date dans les 12 derniers mois. Au-delà, contactez notre expert.
                  </p>
                </div>

                {/* V1.2 : Lien de parenté — dropdown custom sombre cohérent avec la DA cyber */}
                <div className="mb-6">
                  <label className="text-gray-400 text-xs mb-1 block">Votre lien de parenté avec le défunt <span className="text-red-400">*</span></label>
                  <select
                    value={successionData.lien_parente}
                    onChange={(e) => setSuccessionData(prev => ({ ...prev, lien_parente: e.target.value }))}
                    className="w-full bg-gray-900 border border-gray-700 rounded-lg px-4 py-3 text-white focus:border-magenta-400 focus:outline-none font-mono text-sm appearance-none cursor-pointer hover:border-magenta-400/60 transition-colors"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%23FF66CC' stroke-width='2'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 1rem center',
                      paddingRight: '2.5rem',
                    }}
                    required
                  >
                    <option value="" className="bg-gray-900">— Choisissez votre lien —</option>
                    <option value="conjoint" className="bg-gray-900">Conjoint(e) / Époux(se)</option>
                    <option value="enfant" className="bg-gray-900">Enfant</option>
                    <option value="parent" className="bg-gray-900">Parent</option>
                    <option value="frere_soeur" className="bg-gray-900">Frère / Sœur</option>
                    <option value="petit_enfant" className="bg-gray-900">Petit-enfant</option>
                    <option value="neveu_niece" className="bg-gray-900">Neveu / Nièce</option>
                    <option value="autre" className="bg-gray-900">Autre membre de la famille</option>
                    <option value="notaire" className="bg-gray-900">Notaire / Mandataire</option>
                  </select>
                </div>
                
                {/* V1.2 : les organismes sont désormais sélectionnés à l'étape 2
                    Stratégie (avec Je résilie / Je ne touche pas) — bloc legacy
                    conservé caché pour préserver la compat Babel plugin. */}
                <div className="hidden">
                  {KNOWN_ORGANISMS.map((org) => {
                    const isSelected = successionData.organismes.includes(org.id);
                    return (
                      <div
                        key={org.id}
                        onClick={() => {
                          setSuccessionData(prev => ({
                            ...prev,
                            organismes: isSelected
                              ? prev.organismes.filter(o => o !== org.id)
                              : [...prev.organismes, org.id]
                          }));
                        }}
                        className="p-3 rounded-lg"
                      >
                        <p>{org.name}</p>
                        <p>{org.examples}</p>
                      </div>
                    );
                  })}
                  <Input
                    value={successionData.organismes_autres}
                    onChange={(e) => setSuccessionData(prev => ({ ...prev, organismes_autres: e.target.value }))}
                  />
                </div>

                <p className="text-gray-400 text-sm">
                  À l'étape suivante, vous indiquerez pour chaque type de contrat du défunt
                  si vous souhaitez le <span className="text-magenta-400 font-medium">résilier</span>
                  ou le laisser géré par un autre mandataire (notaire, famille…).
                </p>
              </div>
              
              <div className="flex justify-end">
                <Button
                  onClick={() => handleStepChange(2, {
                    // Mandant
                    mandant_prenom: successionData.mandant_prenom,
                    mandant_nom: successionData.mandant_nom,
                    mandant_telephone: successionData.mandant_telephone,
                    mandant_email: successionData.mandant_email,
                    // Défunt
                    prenom_defunt: successionData.prenom_defunt,
                    nom_defunt: successionData.nom_defunt,
                    date_deces: successionData.date_deces,
                    lien: successionData.lien_parente
                  })}
                  disabled={
                    !successionData.mandant_prenom || !successionData.mandant_nom ||
                    !successionData.mandant_telephone || !successionData.mandant_email ||
                    !successionData.prenom_defunt || !successionData.nom_defunt ||
                    !successionData.date_deces || !successionData.lien_parente ||
                    stepSaving
                  }
                  className="bg-magenta-400 text-black hover:bg-magenta-300 font-orbitron"
                >
                  {stepSaving ? 'Sauvegarde...' : 'Continuer vers la Stratégie'} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
              {/* Message d'aide si champs manquants */}
              {(
                !successionData.mandant_prenom || !successionData.mandant_nom ||
                !successionData.mandant_telephone || !successionData.mandant_email ||
                !successionData.prenom_defunt || !successionData.nom_defunt ||
                !successionData.date_deces || !successionData.lien_parente
              ) && (
                <p className="text-orange-400 text-xs flex items-center gap-1 mt-2">
                  <AlertCircle className="w-4 h-4" />
                  Vos coordonnées (prénom, nom, téléphone, email) + infos du défunt (prénom, nom, date de décès, lien de parenté) sont obligatoires
                </p>
              )}
            </motion.div>
          )}

          {/* V1.2 SUCCESSION - Étape 2 : Stratégie (Je résilie / Je ne touche pas) */}
          {type === 'succession' && currentStep === 2 && (
            <motion.div
              key="succession-step2-strategy"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <StrategyStepV526
                mode="succession"
                missionData={missionData}
                onMissionDataChange={updateMissionData}
                onNext={() => updateCurrentStepInMission(3)}
                onBack={() => updateCurrentStepInMission(1)}
              />
            </motion.div>
          )}

          {/* V1.2 SUCCESSION - Étape 3 : Technique (numéros par organisme + acte décès + RIB) */}
          {type === 'succession' && currentStep === 3 && (
            <motion.div
              key="succession-step3-technical"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <TechnicalCollectionStepV526
                mode="succession"
                missionData={missionData}
                onMissionDataChange={updateMissionData}
                onNext={() => updateCurrentStepInMission(4)}
                onBack={() => updateCurrentStepInMission(2)}
              />
            </motion.div>
          )}

          {/* V1.2 SUCCESSION - Étape 4 : Mandat de Gestion (ancien step 3) */}
          {type === 'succession' && currentStep === 4 && (
            <MandatSignature
              missionType="succession"
              missionId={localMissionId}
              userData={user}
              formData={successionData}
              onSigned={async (signResult) => {
                // V1.2 CRITIQUE : Succession doit aussi appeler portal-submit
                // pour créer/mettre à jour le mission_details en BDD. Sans ça
                // la page de gestion retourne "Mission non trouvée".
                const updatedMissionData = {
                  ...missionData,
                  mandat_id: signResult?.mandat_id,
                  signed_at: new Date().toISOString(),
                  status: 'VALIDATED',
                  mission_validated: true,
                  validated_at: new Date().toISOString(),
                  // V1.2 : Mandant + défunt injectés dans missionData pour l'affichage
                  nom_prenom: `${successionData.mandant_prenom} ${successionData.mandant_nom}`.trim(),
                  first_name: successionData.mandant_prenom,
                  last_name: successionData.mandant_nom,
                  telephone: successionData.mandant_telephone,
                  email: successionData.mandant_email,
                  client: {
                    ...(missionData.client || {}),
                    first_name: successionData.mandant_prenom,
                    last_name: successionData.mandant_nom,
                    nom_prenom: `${successionData.mandant_prenom} ${successionData.mandant_nom}`.trim(),
                    telephone: successionData.mandant_telephone,
                    email: successionData.mandant_email,
                  },
                  defunt: {
                    prenom_defunt: successionData.prenom_defunt,
                    nom_defunt: successionData.nom_defunt,
                    date_deces: successionData.date_deces,
                    lien_parente: successionData.lien_parente,
                  },
                };
                updateMissionData(updatedMissionData);

                setSubmitting(true);
                try {
                  const token = localStorage.getItem('token');
                  const payload = {
                    mission_type: type,
                    mission_id: urlMissionId || localMissionId,
                    form_data: {
                      // V1.2 : Infos mandant (l'ayant droit qui signe)
                      mandant_prenom: successionData.mandant_prenom,
                      mandant_nom: successionData.mandant_nom,
                      mandant_telephone: successionData.mandant_telephone,
                      mandant_email: successionData.mandant_email,
                      // Infos client legacy (pour compat avec ValidatedMissionView qui cherche client.*)
                      nom_prenom: `${successionData.mandant_prenom} ${successionData.mandant_nom}`.trim(),
                      first_name: successionData.mandant_prenom,
                      last_name: successionData.mandant_nom,
                      telephone: successionData.mandant_telephone,
                      email: successionData.mandant_email,
                      // Infos défunt
                      prenom_defunt: successionData.prenom_defunt,
                      nom_defunt: successionData.nom_defunt,
                      date_deces: successionData.date_deces,
                      lien_parente: successionData.lien_parente,
                      // Stratégie : catégories choisies
                      strategy_choices: Object.fromEntries(
                        Object.entries(missionData?.categories || {})
                          .filter(([, v]) => v?.status && v.status !== 'none')
                          .map(([k, v]) => [k, {
                            action: v.status === 'desactive' ? 'liquidate' : 'transfer',
                            provider: v.provider || '',
                            data: v.data || {},
                          }])
                      ),
                      // Données techniques par catégorie
                      technical_data: Object.fromEntries(
                        Object.entries(missionData?.categories || {}).map(([k, v]) => [k, v?.data || {}])
                      ),
                      deceased: {
                        first_name: successionData.prenom_defunt,
                        last_name: successionData.nom_defunt,
                        death_date: successionData.date_deces,
                        relation: successionData.lien_parente,
                      },
                    },
                    uploaded_documents: {
                      acte_deces: missionData?.acte_deces,
                      rib_beneficiaire: missionData?.rib_beneficiaire,
                    },
                    status: 'VALIDATED',
                    mission_data: updatedMissionData,
                  };

                  const response = await axios.post(`${API}/missions/portal-submit`, payload, {
                    headers: { 'Authorization': `Bearer ${token}` },
                    withCredentials: true,
                  });

                  toast.success('🎉 Mission Héritage validée ! Votre expert toulousain prend le relais.');
                  const returnedMissionId = response.data.mission_id || urlMissionId || localMissionId;
                  setTimeout(() => {
                    navigate(`/missions/manage?mission=${returnedMissionId}`);
                  }, 1500);
                } catch (err) {
                  console.error('Erreur soumission Succession:', err);
                  toast.error('Erreur lors de la finalisation. Votre mandat est bien signé, rechargez la page.');
                  // Fallback : on tente quand même la redirection pour ne pas bloquer le client
                  setTimeout(() => {
                    navigate(`/missions/manage?mission=${urlMissionId || localMissionId}`);
                  }, 2000);
                } finally {
                  setSubmitting(false);
                }
              }}
              onBack={() => setCurrentStep(3)}
            />
          )}

          {/* V1.2 : ancienne étape 4 Confirmation désactivée — on redirige
              directement vers /missions/manage après signature du mandat.
              Bloc conservé caché pour préserver la compat du plugin Babel. */}
          {false && type === 'succession' && currentStep === 99 && (
            <motion.div
              key="succession-step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="glass-card p-6 border border-success/30 bg-success/5">
                <h2 className="font-orbitron text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Sparkles className="w-5 h-5 text-success" />
                  Récapitulatif du dossier de succession
                </h2>
                
                <div className="space-y-4">
                  <div className="p-4 bg-gray-800/50 rounded-lg">
                    <p className="text-gray-500 text-xs font-mono mb-1">DÉFUNT</p>
                    <p className="text-white font-medium">{successionData.nom_defunt}</p>
                    <p className="text-gray-400 text-sm">
                      Décédé le {successionData.date_deces ? new Date(successionData.date_deces).toLocaleDateString('fr-FR') : ''}
                    </p>
                    <p className="text-magenta-400 text-xs mt-1">Lien de parenté : {successionData.lien_parente}</p>
                  </div>
                  
                  <div className="p-4 bg-gray-800/50 rounded-lg">
                    <p className="text-gray-500 text-xs font-mono mb-2">ORGANISMES À CONTACTER</p>
                    <div className="flex flex-wrap gap-2">
                      {successionData.organismes.map(o => (
                        <span key={o} className="px-3 py-1 bg-magenta-400/20 text-magenta-400 rounded-full text-sm">
                          {KNOWN_ORGANISMS.find(x => x.id === o)?.name}
                        </span>
                      ))}
                    </div>
                  </div>
                  
                  <div className="p-4 bg-gray-800/50 rounded-lg">
                    <p className="text-gray-500 text-xs font-mono mb-2">DOCUMENTS FOURNIS</p>
                    <div className="space-y-2">
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-success" />
                        <span className="text-white">Acte de décès</span>
                      </div>
                      {uploadedDocs.rib_beneficiaire && (
                        <div className="flex items-center gap-2">
                          <CheckCircle className="w-4 h-4 text-success" />
                          <span className="text-white">RIB bénéficiaire</span>
                        </div>
                      )}
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-success" />
                        <span className="text-white">Mandat de gestion signé</span>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div className="mt-6 p-4 bg-magenta-400/10 border border-magenta-400/30 rounded-lg">
                  <p className="text-magenta-400 text-sm">
                    <Shield className="w-4 h-4 inline mr-2" />
                    Nous traiterons votre dossier avec la plus grande bienveillance et confidentialité.
                  </p>
                </div>
              </div>
              
              <div className="flex justify-between">
                <Button variant="outline" onClick={() => setCurrentStep(3)} className="border-gray-600">
                  <ArrowLeft className="w-4 h-4 mr-2" /> Modifier
                </Button>
                <Button 
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-success text-black hover:bg-success/90 font-orbitron px-8"
                  data-testid="submit-succession-btn"
                >
                  {submitting ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : (
                    <>Envoyer le dossier <ArrowRight className="w-4 h-4 ml-2" /></>
                  )}
                </Button>
              </div>
            </motion.div>
          )}
          
          {/* ═══════════════════════════════════════════════════════════════ */}
          {/* AUDIT - Étape 1 : Plan d'Action (pas de questionnaire, direct action) */}
          {/* ═══════════════════════════════════════════════════════════════ */}
          {type === 'audit' && currentStep === 1 && (
            <motion.div
              key="audit-step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              className="space-y-6"
            >
              <div className="glass-card p-6 border border-success/30">
                <h2 className="font-orbitron text-xl font-bold text-white mb-6 flex items-center gap-2">
                  <Zap className="w-5 h-5 text-success" />
                  Votre Plan d'Action Personnalisé
                </h2>
                
                <p className="text-gray-400 mb-6">
                  Bienvenue dans votre espace d'audit. Nous allons analyser vos abonnements et vous proposer des optimisations.
                </p>
                
                <div className="p-4 bg-success/10 border border-success/30 rounded-lg mb-6">
                  <p className="text-success text-sm flex items-center gap-2">
                    <CheckCircle className="w-4 h-4" />
                    Votre audit est en cours de traitement par notre équipe d'experts.
                  </p>
                </div>
                
                <div className="space-y-4">
                  <div className="p-4 bg-gray-800/50 rounded-lg border border-gray-700">
                    <h3 className="text-white font-medium mb-2">Prochaines étapes :</h3>
                    <ul className="text-gray-400 text-sm space-y-2">
                      <li className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-success/20 text-success text-xs flex items-center justify-center">1</span>
                        Analyse de vos contrats actuels
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-gray-700 text-gray-400 text-xs flex items-center justify-center">2</span>
                        Identification des économies potentielles
                      </li>
                      <li className="flex items-center gap-2">
                        <span className="w-6 h-6 rounded-full bg-gray-700 text-gray-400 text-xs flex items-center justify-center">3</span>
                        Propositions d'optimisation personnalisées
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
              
              <div className="flex justify-end">
                <Button 
                  onClick={() => navigate('/dashboard?tab=overview')}
                  className="bg-success text-black hover:bg-success/90 font-orbitron"
                >
                  Retour au tableau de bord <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </main>
      
      {/* 🤖 PHASE D : Helper proactif (affiché après 45s d'inactivité) */}
      <AnimatePresence>
        {showHelper && (
          <motion.div
            initial={{ opacity: 0, y: 20, scale: 0.9 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.9 }}
            className="fixed bottom-6 right-6 max-w-sm z-50"
          >
            <div className="glass-card p-4 border border-cyan-400/30 shadow-lg shadow-cyan-400/10">
              <button 
                onClick={() => setShowHelper(false)}
                className="absolute top-2 right-2 text-gray-500 hover:text-white"
              >
                <X className="w-4 h-4" />
              </button>
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-cyan-400/20 flex items-center justify-center flex-shrink-0">
                  <MessageCircle className="w-5 h-5 text-cyan-400" />
                </div>
                <div>
                  <p className="text-white text-sm font-medium mb-1">Besoin d'aide ?</p>
                  <p className="text-gray-400 text-sm">
                    {proactiveMessage || (type === 'demenagement' 
                      ? "Un doute sur votre numéro de compteur ou votre date de préavis ? Je connais les délais de tous les fournisseurs."
                      : type === 'succession'
                        ? "Je sais que cette période est difficile. Je suis là pour vous aider à lister les organismes sans rien oublier."
                        : "Besoin d'aide pour numériser vos documents ? Je peux vous expliquer comment faire avec votre smartphone."
                    )}
                  </p>
                  <Button 
                    size="sm" 
                    className="mt-3 bg-cyan-400/20 text-cyan-400 hover:bg-cyan-400/30"
                    onClick={() => {
                      setShowHelper(false);
                      // Ouvrir le chatbot
                      window.dispatchEvent(new CustomEvent('openChatbot'));
                    }}
                  >
                    <MessageCircle className="w-4 h-4 mr-2" />
                    Poser une question
                  </Button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  </div>
  );
};

// V5.20 : Export avec ErrorBoundary intégré
const MissionPortalPageWithErrorBoundary = () => (
  <ErrorBoundary>
    <MissionPortalPage />
  </ErrorBoundary>
);

export default MissionPortalPageWithErrorBoundary;
